<script lang="ts">
    import Data from "@reperio-news/core.components/editorBlocks/ImageGallery/Data";
    import ImageView from "@reperio-news/core.components/ImageView.svelte";
    import { mediaCredit } from "@reperio-news/core.components/includes/Media.js";
    import { onMount, onDestroy } from "svelte";
    import { fade } from "svelte/transition";
    import { swipe } from "svelte-gestures";
    import NavButton from "./NavButton.svelte";
    import { z } from "zod";
    export let id: string;
    export let index: number = 0;
    export let data: z.infer<typeof Data>;
    export let onClose: () => void;

    let closeButton: HTMLButtonElement;

    function track() {
        const url = new URL(window.location.href);
        const ha = new URLSearchParams(url.hash.substring(1));
        ha.set("gal", id);
        ha.set("gal_i", index.toString());
        ha.set("gal_fs", "1");
        url.hash = ha.toString();
        window.history.replaceState({}, "", url);

        // @ts-ignore
        if (typeof SATI_TrackView === "function") {
            // @ts-ignore
            SATI_TrackView(document.location.href, document.referrer);
        }
    }

    function nav(direction: -1 | 1): void {
        if (!data.images || !data.images[index + direction]) {
            return;
        }

        index += direction;
        track();
    }

    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "ArrowLeft") {
            nav(-1);
        } else if (e.key === "ArrowRight") {
            nav(1);
        } else if (e.key === "Escape") {
            onClose();
        } else if (e.key === " ") {
            e.preventDefault();
            nav(1);
        }
    };

    const handleSwipe = (e: any) => {
        switch (e.detail.direction) {
            case "left":
                nav(1);
                break;
            case "right":
                nav(-1);
                break;
            case "top":
            case "bottom":
                onClose();
                break;
        }
    };

    onMount(() => {
        track();
        document.body.classList.add("no-scroll");
        closeButton?.focus();
    });

    onDestroy(() => {
        document.body.classList.remove("no-scroll");
    });
</script>

<svelte:window on:keydown={handleKeyDown} />

<!-- svelte-ignore a11y_click_events_have_key_events -->
<div
    class="container"
    role="dialog"
    aria-modal="true"
    aria-label="Galerie foto"
    tabindex="-1"
    on:click|self={onClose}
>
    {#if data.images && data.images.length}
        <div class="topbar">
            <button type="button" class="back" on:click={onClose}>
                ← Înapoi la articol
            </button>

            <div class="n">{index + 1} / {data.images.length}</div>

            <button
                type="button"
                class="close"
                bind:this={closeButton}
                aria-label="Închide galeria (Esc)"
                title="Închide (Esc)"
                on:click={onClose}
            >
                <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    aria-hidden="true"
                >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
            </button>
        </div>

        <div
            class="main"
            use:swipe={{
                timeframe: 300,
                minSwipeDistance: 60,
                touchAction: "none",
            }}
            on:swipe={handleSwipe}
        >
            {#key index}
                <div class="slide" transition:fade={{ duration: 150 }}>
                    <ImageView
                        src={data.images[index]}
                        widths={[1500, 1000, 800, 500]}
                        heights={[1500, 1000, 800, 500]}
                        fit="contain"
                    />
                </div>
            {/key}

            {#if data.images[index - 1]}
                <NavButton
                    direction="prev"
                    label="Imaginea anterioară"
                    onClick={() => nav(-1)}
                />
            {/if}

            {#if data.images[index + 1]}
                <NavButton
                    direction="next"
                    label="Imaginea următoare"
                    onClick={() => nav(1)}
                />
            {/if}
        </div>

        {#if data.images[index].title || mediaCredit(data.images[index], data.images[index])}
            {@const credit = mediaCredit(data.images[index], data.images[index])}
            <div class="caption">
                {#if data.images[index].title}{data.images[index].title}{/if}
                {#if credit}
                    <span class="source">
                        Foto:
                        {#if credit.url}
                            <a href={credit.url} target="_blank" rel="nofollow noopener">{credit.text}</a>
                        {:else}
                            {credit.text}
                        {/if}
                    </span>
                {/if}
            </div>
        {/if}

        <div class="preload" aria-hidden="true">
            {#if data.images[index - 1]}
                <ImageView
                    src={data.images[index - 1]}
                    widths={[1500, 1000, 800, 500]}
                    heights={[1500, 1000, 800, 500]}
                    fit="contain"
                    loading="eager"
                />
            {/if}
            {#if data.images[index + 1]}
                <ImageView
                    src={data.images[index + 1]}
                    widths={[1500, 1000, 800, 500]}
                    heights={[1500, 1000, 800, 500]}
                    fit="contain"
                    loading="eager"
                />
            {/if}
        </div>
    {/if}
</div>

<style lang="scss">
    /* scroll-lock must not depend on the theme defining .no-scroll —
       only adevarul.ro ever did, the other sites scrolled behind the overlay */
    :global(body.no-scroll) {
        overflow: hidden;
    }

    @keyframes overlayIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }

    .container {
        user-select: none;
        position: fixed;
        inset: 0;
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        background-color: rgba(10, 10, 10, 0.97);
        color: #fff;
        z-index: 99999;
        animation: overlayIn 0.16s ease-out;

        .topbar {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 10px;
            padding: 12px 16px;

            .back {
                border: 1px solid rgba(255, 255, 255, 0.3);
                border-radius: 999px;
                background: none;
                color: #fff;
                font-size: 14px;
                line-height: 1;
                padding: 9px 14px;
                cursor: pointer;
                transition: background-color 0.15s ease;

                &:hover {
                    background-color: rgba(255, 255, 255, 0.12);
                }

                &:focus-visible {
                    outline: 2px solid #fff;
                    outline-offset: 2px;
                }
            }

            .n {
                color: #bbb;
                font-size: 14px;
                letter-spacing: 0.04em;
            }

            .close {
                width: 40px;
                height: 40px;
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 0;
                border: none;
                border-radius: 50%;
                background-color: rgba(255, 255, 255, 0.1);
                color: #fff;
                cursor: pointer;
                transition: background-color 0.15s ease;

                &:hover {
                    background-color: rgba(255, 255, 255, 0.25);
                }

                &:focus-visible {
                    outline: 2px solid #fff;
                    outline-offset: 2px;
                }

                svg {
                    width: 18px;
                    height: 18px;
                }
            }
        }

        .main {
            position: relative;
            flex: 1;
            min-height: 0;
            width: calc(100% - 20px);
            max-width: 1500px;
            margin: 0 auto;

            .slide {
                position: absolute;
                inset: 0;

                :global(picture),
                :global(img) {
                    display: block;
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                }
            }
        }

        .caption {
            padding: 14px 20px 20px 20px;
            max-width: 900px;
            margin: 0 auto;
            color: #ddd;
            font-size: 15px;
            line-height: 1.5;
            text-align: center;
            font-style: italic;
        }

        .caption .source {
            opacity: 0.75;
            font-size: 0.9em;
            margin-left: 0.25em;
        }

        .caption .source a {
            color: inherit;
            text-decoration: underline;
        }

        .preload {
            display: none;
        }
    }
</style>
